import React, { useState } from 'react';
import { Upload, AlertTriangle, ShieldAlert, Lock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { FloatingElements } from './FloatingElements';
import { downloadSampleReport } from '../../utils/sampleReportGenerator';
interface HeroSectionProps {
  h1Override?: React.ReactNode;
}
export const HeroSection = ({
  h1Override
}: HeroSectionProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };
  const handleDownloadSampleReport = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadSampleReport();
    } catch (error) {
      console.error('Failed to download sample report:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  return <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 bg-[#050505] overflow-hidden min-h-[90vh] flex flex-col justify-center" data-id="element-2617">
      <FloatingElements data-id="element-2618" />

      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none z-0" data-id="element-2619" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center w-full" data-id="element-2620">
        {/* Animated Status Badge */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 rounded-full border border-[#FF0000]/30 mb-8 shadow-[0_0_15px_rgba(255,0,0,0.15)] backdrop-blur-sm" data-id="element-2621">
          <motion.div animate={{
          opacity: [1, 0.4, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} data-id="element-2622">
            <AlertTriangle size={14} strokeWidth={2.5} data-id="element-2623" />
          </motion.div>
          <span className="font-mono text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase" data-id="element-2624">
            Forensic AI Analysis Active
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.05] tracking-tight max-w-5xl mx-auto" data-id="element-2625">
          {h1Override || <>
              Decode the patterns hiding in your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF0000] via-[#FF4444] to-[#FFD700]" data-id="element-2626">
                conversations
              </span>
              .
            </>}
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-base md:text-lg text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-mono" data-id="element-2627">
          Upload your chats and let{' '}
          <span className="text-[#FFD700] font-bold" data-id="element-2628">forensic AI</span> surface
          manipulation, gaslighting, and emotional abuse —{' '}
          <span className="text-white" data-id="element-2629">
            with the clarity of a clinical analyst
          </span>
          .
        </motion.p>

        {/* Real Scan Result Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="relative max-w-[480px] mx-auto mb-10 group" data-id="element-2630">
          <div className="absolute -inset-1 bg-[#FF0000]/20 rounded-xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2631" />
          <div className="relative rounded-xl overflow-hidden border border-[#FF0000]/30 shadow-[0_0_30px_rgba(255,0,0,0.18)] bg-black/50 backdrop-blur-sm" data-id="element-2632">
            <img src="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" alt="Toxicity Detected 92% — Forensic Scan Result" className="w-full h-auto object-cover opacity-90" fetchPriority="high" width={480} height={380} decoding="async" data-id="element-2633" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-4 flex justify-between items-end" data-id="element-2634">
              <span className="text-[#FF0000] font-mono text-[10px] tracking-[0.2em] font-bold uppercase flex items-center gap-2" data-id="element-2635">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse shadow-[0_0_6px_#FF0000]" data-id="element-2636" />
                Real Scan Result
              </span>
              <span className="text-white/50 font-mono text-[10px] tracking-[0.2em]" data-id="element-2637">
                ID: #8X92-A
              </span>
            </div>
          </div>
        </motion.div>

        {/* Forensic Scanner Widget */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.45
      }} className="relative max-w-2xl mx-auto mb-10" data-id="element-2638">
          {/* Outer glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0000] via-[#FFD700] to-[#FF0000] rounded-2xl opacity-20 blur-xl animate-pulse" data-id="element-2639" />

          <div className="relative bg-[#0a0a0a] rounded-xl border border-[#FF0000]/30 p-1 overflow-hidden shadow-2xl" data-id="element-2640">
            {/* Corner Markers */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000] z-20 rounded-tl-lg" data-id="element-2641" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000] z-20 rounded-tr-lg" data-id="element-2642" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000] z-20 rounded-bl-lg" data-id="element-2643" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000] z-20 rounded-br-lg" data-id="element-2644" />

            <div className="bg-[#050505] rounded-lg p-8 md:p-12 text-center relative overflow-hidden" data-id="element-2645">
              {/* Scanning Line Animation */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000,0_0_10px_#FF0000] z-10 animate-[scanline_3s_ease-in-out_infinite]" data-id="element-2646" />
              <style data-id="element-2647">{`
                @keyframes scanline {
                  0% { top: 0%; opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { top: 100%; opacity: 0; }
                }
                @keyframes dropglow {
                  0%, 100% { box-shadow: 0 0 20px rgba(255,0,0,0.25), inset 0 0 20px rgba(255,0,0,0.1); }
                  50% { box-shadow: 0 0 35px rgba(255,0,0,0.5), inset 0 0 25px rgba(255,0,0,0.2); }
                }
              `}</style>

              {/* Inner grid */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-2648" />

              <input type="file" id="hero-file-upload" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" data-id="element-2649" />
              <label htmlFor="hero-file-upload" className="cursor-pointer block relative z-20 group/label" data-id="element-2650">
                <motion.div animate={{
                scale: [1, 1.06, 1]
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="inline-flex items-center justify-center w-20 h-20 bg-[#FF0000]/10 rounded-full mb-6 border border-[#FF0000]/50 group-hover/label:bg-[#FF0000]/20 transition-colors" style={{
                animation: 'dropglow 2.5s ease-in-out infinite'
              }} data-id="element-2651">
                  <Upload className="w-8 h-8 text-[#FF0000]" strokeWidth={2} data-id="element-2652" />
                </motion.div>

                <h3 className="text-2xl md:text-3xl font-black text-white mb-3 uppercase tracking-wide group-hover/label:text-[#FF0000] transition-colors" data-id="element-2653">
                  Drop Evidence Here
                </h3>
                <p className="text-gray-400 text-sm mb-8 font-mono max-w-md mx-auto leading-relaxed" data-id="element-2654">
                  Analyzing for manipulation, narcissism, and "The Silent Drain"
                  patterns.
                </p>

                {selectedFile ? <div className="inline-flex items-center gap-2 bg-[#FF0000]/20 border border-[#FF0000] px-4 py-2 rounded-lg text-[#FF0000] font-mono text-sm font-bold" data-id="element-2655">
                    <ShieldAlert size={16} data-id="element-2656" />
                    <span className="truncate max-w-[280px]" data-id="element-2657">
                      EVIDENCE LOADED: {selectedFile.name}
                    </span>
                  </div> : <div className="inline-flex items-center gap-2 text-xs text-gray-500 font-mono border border-gray-800 px-4 py-2 rounded-lg bg-black/50 backdrop-blur-sm" data-id="element-2658">
                    <Lock size={12} data-id="element-2659" />
                    <span className="tracking-[0.15em]" data-id="element-2660">
                      100% ANONYMOUS &amp; ENCRYPTED
                    </span>
                  </div>}
              </label>
            </div>
          </div>

          {/* Scanner Status Row */}
          <div className="flex justify-between items-center mt-4 px-2" data-id="element-2661">
            <span className="text-[10px] text-[#FF0000] font-mono flex items-center gap-2 tracking-[0.2em] uppercase" data-id="element-2662">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] shadow-[0_0_6px_#FF0000] animate-pulse" data-id="element-2663" />
              System Active
            </span>
            <span className="text-[10px] text-gray-600 font-mono tracking-[0.2em] uppercase" data-id="element-2664">
              V.2.4.0 Forensic Engine
            </span>
          </div>
        </motion.div>

        {/* CTA Row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12" data-id="element-2665">
          <a href="/signup" className="group relative px-8 py-4 bg-[#FF0000] text-white font-bold text-base uppercase tracking-[0.15em] overflow-hidden rounded-xl hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.3)] hover:shadow-[0_0_40px_rgba(255,0,0,0.5)] min-w-[240px] hover:-translate-y-0.5 inline-flex items-center justify-center gap-2" data-id="element-2666">
            <span data-id="element-2667">Start Forensic Scan</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-2668" />
          </a>

          <button onClick={handleDownloadSampleReport} disabled={isDownloading} className="px-8 py-4 bg-transparent border border-gray-800 text-gray-300 font-bold text-base uppercase tracking-[0.15em] rounded-xl hover:border-[#FFD700] hover:text-[#FFD700] transition-all min-w-[240px] hover:-translate-y-0.5 hover:bg-[#FFD700]/5 disabled:opacity-50 disabled:cursor-wait" data-id="element-2669">
            {isDownloading ? 'Generating PDF…' : 'View Sample Report'}
          </button>
        </motion.div>

        {/* Live Stats Row */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.85
      }} className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8 text-xs sm:text-sm text-gray-500 font-mono" data-id="element-2670">
          <div className="flex items-center gap-3" data-id="element-2671">
            <span className="relative flex h-2 w-2" data-id="element-2672">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD700] opacity-75" data-id="element-2673" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFD700]" data-id="element-2674" />
            </span>
            <span className="tracking-[0.15em] uppercase" data-id="element-2675">
              <span className="text-[#FFD700] font-bold" data-id="element-2676">500+</span>{' '}
              <span className="text-gray-400" data-id="element-2677">Scans This Hour</span>
            </span>
          </div>

          <div className="hidden sm:block w-px h-4 bg-gray-800" data-id="element-2678" />

          <div className="tracking-[0.15em] uppercase" data-id="element-2679">
            <span className="text-white font-bold" data-id="element-2680">4.9/5</span>{' '}
            <span className="text-gray-500" data-id="element-2681">Accuracy Rating</span>
          </div>

          <div className="hidden sm:block w-px h-4 bg-gray-800" data-id="element-2682" />

          <div className="flex items-center gap-2 tracking-[0.15em] uppercase" data-id="element-2683">
            <Lock size={12} className="text-gray-600" data-id="element-2684" />
            <span data-id="element-2685">AES-256 Encryption</span>
          </div>
        </motion.div>
      </div>
    </section>;
};